import type { ModuleTheme } from "@proof/core";

/** Integration style presets. They only style embedded integration pieces. */
export const integrationThemePresets = ["default", "propago"] as const;
export type IntegrationThemePreset = (typeof integrationThemePresets)[number];

export interface RegisteredPreset {
  tokens: ModuleTheme;
  css: (theme: Required<ModuleTheme>, scope: string) => string;
}
/**
 * Presets other than "default" are delivered by access-controlled plugin
 * modules, which register themselves here once loaded.
 */
// Shared across separately built bundles (the SDK and its plugin modules).
const registry: Map<string, RegisteredPreset> = ((
  globalThis as { __proofIntegrationPresets?: Map<string, RegisteredPreset> }
).__proofIntegrationPresets ??= new Map());
export function registerIntegrationPreset(name: string, preset: RegisteredPreset) {
  registry.set(name, preset);
}
export function registeredIntegrationPreset(name: string) {
  return registry.get(name);
}
/** Plugin module that provides each gated preset. */
export const presetPluginModule: Record<string, string> = {
  propago: "esm-project-propago-theme",
};

const loading = new Map<string, Promise<boolean>>();
/** Load a gated preset's plugin; resolves false when access is denied. */
export function loadIntegrationPreset(
  name: string,
  url: (module: string) => string,
): Promise<boolean> {
  if (name === "default" || registry.has(name)) return Promise.resolve(true);
  const module = presetPluginModule[name];
  if (!module) return Promise.resolve(false);
  if (!loading.has(name))
    loading.set(
      name,
      import(/* @vite-ignore */ url(module)).then(
        () => registry.has(name),
        () => false,
      ),
    );
  return loading.get(name)!;
}
